import type { UsersItemProps } from 'components/user/Records/components/UsersItem.tsx';
import { UsersItem } from 'components/user/Records/components/UsersItem.tsx';
import { get, useFormContext } from 'react-hook-form';
import { memo, useCallback, useRef } from 'react';
import type { FormItemDefinition } from 'types';

export type UsersItemEditableProps = {
  id: string;
  definition?: FormItemDefinition;
  agendaId?: string;
} & UsersItemProps;

const UsersItemEditableBase = (props: UsersItemEditableProps) => {
  const {
    formState: { errors },
  } = useFormContext();
  // UsersItem commits from a modal, so no table input blurs and the table onBlurCapture
  // (calculate, save, joined parent sync) would not run. This input carries the field path;
  // a bubbling focusout after setValue is what starts that handler.
  const blurBridgeRef = useRef<HTMLInputElement | null>(null);

  const handleConfirm = useCallback(() => {
    const blurBridgeInput = blurBridgeRef.current;
    if (!blurBridgeInput) return;

    let focusOutEvent: Event;
    try {
      focusOutEvent = new FocusEvent('focusout', { bubbles: true });
    } catch {
      focusOutEvent = new Event('focusout', { bubbles: true });
    }

    blurBridgeInput.dispatchEvent(focusOutEvent);
  }, []);

  return (
    <>
      <input
        ref={blurBridgeRef}
        name={props.id}
        readOnly
        tabIndex={-1}
        aria-hidden
        value=""
        style={{
          position: 'fixed',
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: 'none',
        }}
      />
      <UsersItem error={get(errors, props.id)} detail={false} {...props} formOptionShouldDirty onConfirm={handleConfirm} />
    </>
  );
};

export const UsersItemEditable = memo(UsersItemEditableBase) as typeof UsersItemEditableBase;
